// src/pages/tuck_shop/MenuItemList.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Box, Typography, Grid, Button, Chip, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  FormControl, InputLabel, Select, FormHelperText, LinearProgress,
  Alert, Snackbar, TablePagination, InputAdornment, CircularProgress,
  Fab, Fade, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, Card, CardContent, useTheme,
  alpha, ToggleButton, ToggleButtonGroup, Slider, Stepper, Step, StepLabel,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Visibility as ViewIcon, RestaurantMenu as MenuIcon,
  CalendarToday as CalendarIcon, Search as SearchIcon,
  Clear as ClearIcon, Save as SaveIcon, Close as CloseIcon,
  Refresh as RefreshIcon, Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon, Warning as WarningIcon,
  Info as InfoIcon, AttachMoney as MoneyIcon,
  Category as CategoryIcon, LocalDining as FoodIcon,
  LocalDrink as DrinkIcon, FilterList as FilterIcon,
  Inventory as InventoryIcon, AddShoppingCart as StockIcon,
  History as HistoryIcon, ArrowUpward as InIcon,
  ArrowDownward as OutIcon, Store as StoreIcon,
} from '@mui/icons-material';

import { ACCE_BRAND } from '../../theme/colors';
import Sidebar from '../../components/tuck_shop/Sidebar';
import Header from '../../components/tuck_shop/Header';
import Footer from '../../components/tuck_shop/Footer';

// ════════════════════════════════════════════
// AXIOS CLIENT
// ════════════════════════════════════════════
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
const API_PREFIX   = process.env.REACT_APP_API_PREFIX   || '/api';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

const api = axios.create({
  baseURL: FULL_API_URL,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const extractError = (err) => {
  const d = err?.response?.data;
  if (!d) return err.message || 'Request failed';
  if (typeof d === 'string') return d;
  if (d.errors) {
    const first = Object.values(d.errors)[0];
    return Array.isArray(first) ? first[0] : String(first);
  }
  return d.message || 'Request failed';
};

// ── Products ──
const apiListProducts = async (params = {}) => {
  try {
    const { data } = await api.get('/tuckshop/products', { params });
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiGetProductStats = async () => {
  try {
    const { data } = await api.get('/tuckshop/products/stats');
    return { success: true, stats: data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiGetCategories = async () => {
  try {
    const { data } = await api.get('/tuckshop/products/categories');
    return { success: true, categories: data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiCreateProduct = async (payload) => {
  try {
    const { data } = await api.post('/tuckshop/products', payload);
    return { success: true, product: data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiUpdateProduct = async (id, payload) => {
  try {
    const { data } = await api.put(`/tuckshop/products/${id}`, payload);
    return { success: true, product: data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiToggleProduct = async (id) => {
  try {
    const { data } = await api.patch(`/tuckshop/products/${id}/toggle`);
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiDeleteProduct = async (id) => {
  try {
    await api.delete(`/tuckshop/products/${id}`);
    return { success: true };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

// ── Inventory ──
const apiGetProductStock = async (productId) => {
  try {
    const { data } = await api.get(`/tuckshop/products/${productId}/stock`);
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiAddProductStock = async (productId, payload) => {
  try {
    const { data } = await api.post(`/tuckshop/products/${productId}/stock`, payload);
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiGetInventoryStats = async () => {
  try {
    const { data } = await api.get('/tuckshop/inventory/stats');
    return { success: true, stats: data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

// ════════════════════════════════════════════
// CONSTANTS
// ════════════════════════════════════════════
const TUCKSHOP = {
  name: 'Tuckshop',
  color: ACCE_BRAND.orange,
  lightColor: ACCE_BRAND.orangeLight,
  icon: '🏪',
};

const MAIN_CATEGORIES = ['Food', 'Drinks'];
const SELECT_MIN_WIDTH = 160;

const STATUS_META = {
  IN_STOCK:     { label: 'In Stock',     bg: '#4caf50', text: '#2e7d32' },
  LOW_STOCK:    { label: 'Low Stock',    bg: '#ff9800', text: '#e65100' },
  OUT_OF_STOCK: { label: 'Out of Stock', bg: '#f44336', text: '#c62828' },
};

// ════════════════════════════════════════════
// SUB-COMPONENTS
// ════════════════════════════════════════════

const StatsCard = React.memo(({ stats, locationColor }) => {
  const statItems = [
    { label: 'Total Items', value: stats.total,       color: locationColor, icon: <MenuIcon sx={{ fontSize: 20 }} /> },
    { label: 'Active',      value: stats.active,      color: '#4caf50',     icon: <CheckCircleIcon sx={{ fontSize: 20 }} /> },
    { label: 'Inactive',    value: stats.inactive,    color: '#f44336',     icon: <CancelIcon sx={{ fontSize: 20 }} /> },
    { label: 'Food Items',  value: stats.food || 0,   color: '#FF6F00',     icon: <FoodIcon sx={{ fontSize: 20 }} /> },
    { label: 'Drinks',      value: stats.drinks || 0, color: '#00695C',     icon: <DrinkIcon sx={{ fontSize: 20 }} /> },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {statItems.map((item) => (
        <Grid item xs={12} sm={6} md={4} lg={2.4} key={item.label}>
          <Card
            sx={{
              borderRadius: 2,
              borderLeft: `4px solid ${item.color}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              },
            }}
          >
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
                  {item.label}
                </Typography>
                {item.icon}
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: item.color }}>
                {item.value}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
});

const MenuItemRow = React.memo(({
  item, onEdit, onDelete, onToggle, onView, onAddStock, onViewStock, index,
}) => {
  const theme = useTheme();
  const statusColor = item.is_active ? '#4caf50' : '#f44336';
  const statusLabel = item.is_active ? 'Active' : 'Inactive';
  const mainCategoryColor = item.main_category === 'Food' ? '#FF6F00' : '#00695C';
  const price = Number(item.price) || 0;

  return (
    <TableRow
      sx={{
        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
        transition: 'background-color 0.2s ease',
      }}
    >
      <TableCell sx={{ fontWeight: 600 }}>{index + 1}</TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MenuIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>{item.category}</Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={item.main_category}
          size="small"
          sx={{
            fontWeight: 700,
            backgroundColor: alpha(mainCategoryColor, 0.1),
            color: mainCategoryColor,
            fontSize: '0.75rem',
          }}
          icon={item.main_category === 'Food'
            ? <FoodIcon sx={{ fontSize: 14 }} />
            : <DrinkIcon sx={{ fontSize: 14 }} />}
        />
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" fontWeight={700} color="primary">
          ₦{price.toFixed(2)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={400}
          sx={{ fontSize: '0.85rem', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {item.description || '-'}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={statusLabel}
          size="small"
          sx={{ backgroundColor: alpha(statusColor, 0.1), color: statusColor, fontWeight: 700, fontSize: '0.75rem' }}
        />
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => onView('view', item)} sx={{ color: theme.palette.info.main }}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit('edit', item)} sx={{ color: theme.palette.primary.main }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Stock">
            <IconButton size="small" onClick={() => onAddStock(item)} sx={{ color: '#4caf50' }}>
              <StockIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Stock">
            <IconButton size="small" onClick={() => onViewStock(item)} sx={{ color: theme.palette.info.main }}>
              <StoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={item.is_active ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={() => onToggle(item)}
              sx={{ color: item.is_active ? theme.palette.warning.main : theme.palette.success.main }}
            >
              {item.is_active ? <CancelIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Permanently Delete">
            <IconButton size="small" onClick={() => onDelete(item)} sx={{ color: theme.palette.error.main }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
});

// ════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════
const MenuItemList = () => {
  const theme = useTheme();

  // ── State ──
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeMenu, setActiveMenu] = useState('Menu');
  const [categories, setCategories] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, food: 0, drinks: 0 });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');

  const [mainCategoryFilter, setMainCategoryFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [isPriceFilterActive, setIsPriceFilterActive] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [selectedItem, setSelectedItem] = useState(null);

  const defaultFormData = useMemo(() => ({
    category: '',
    name: '',
    main_category: 'Food',
    description: '',
    price: '',
    is_active: true,
    created_date: new Date().toISOString().split('T')[0],
  }), []);

  const [formData, setFormData] = useState(defaultFormData);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [itemToToggle, setItemToToggle] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  // ── Stock dialogs ──
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [stockItem, setStockItem] = useState(null);
  const [stockQuantity, setStockQuantity] = useState('');
  const [stockCost, setStockCost] = useState('');
  const [stockSupplier, setStockSupplier] = useState('');
  const [stockNotes, setStockNotes] = useState('');
  const [stockStep, setStockStep] = useState(0);
  const [stockSubmitting, setStockSubmitting] = useState(false);

  const [viewStockDialogOpen, setViewStockDialogOpen] = useState(false);
  const [viewStockItem, setViewStockItem] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);
  const [stockMovements, setStockMovements] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const locationConfig = TUCKSHOP;
  const { name: locationName, color: locationColor } = locationConfig;

  // ── Snackbar ──
  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  // ── Debounce search ──
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // ── Fetch products ──
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const params = {
      page: page + 1,
      page_size: rowsPerPage,
      sort_by: orderBy,
      sort_dir: order,
    };
    if (debouncedSearch)              params.search = debouncedSearch;
    if (mainCategoryFilter !== 'all') params.main_category = mainCategoryFilter;
    if (categoryFilter !== 'all')     params.category = categoryFilter;
    if (isPriceFilterActive) {
      params.price_min = priceRange[0];
      params.price_max = priceRange[1];
    }

    const res = await apiListProducts(params);
    if (res.success) {
      setItems(res.data || []);
      setTotalCount(res.total || 0);
    } else {
      showSnackbar(res.error || 'Failed to load products', 'error');
      setItems([]);
      setTotalCount(0);
    }
    setLoading(false);
  }, [page, rowsPerPage, orderBy, order, debouncedSearch,
      mainCategoryFilter, categoryFilter, isPriceFilterActive, priceRange, showSnackbar]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // ── Fetch stats ──
  const fetchStats = useCallback(async () => {
    const res = await apiGetProductStats();
    if (res.success) setStats(res.stats);
  }, []);

  // ── Fetch categories ──
  const fetchCategories = useCallback(async () => {
    const res = await apiGetCategories();
    if (res.success) setCategories(res.categories || []);
  }, []);

  useEffect(() => {
    fetchStats();
    fetchCategories();
  }, [fetchStats, fetchCategories]);

  // ── Create ──
  const handleCreateMenuItem = useCallback(async (data) => {
    const res = await apiCreateProduct(data);
    if (!res.success) { showSnackbar(res.error, 'error'); return false; }
    showSnackbar(`✅ "${res.product.name}" created successfully!`);
    handleCloseDialog();
    fetchProducts();
    fetchStats();
    fetchCategories();
    return true;
  }, [showSnackbar, fetchProducts, fetchStats, fetchCategories]);

  // ── Update ──
  const handleUpdateMenuItem = useCallback(async (id, data) => {
    const res = await apiUpdateProduct(id, data);
    if (!res.success) { showSnackbar(res.error, 'error'); return false; }
    showSnackbar(`✅ "${res.product.name}" updated successfully!`);
    handleCloseDialog();
    fetchProducts();
    fetchStats();
    return true;
  }, [showSnackbar, fetchProducts, fetchStats]);

  // ── Toggle ──
  const handleToggleMenuItemStatus = useCallback(async (id) => {
    const res = await apiToggleProduct(id);
    if (!res.success) { showSnackbar(res.error, 'error'); return; }
    showSnackbar(`✅ "${res.product.name}" ${res.product.is_active ? 'activated' : 'deactivated'} successfully!`);
    handleCloseToggleDialog();
    fetchProducts();
    fetchStats();
  }, [showSnackbar, fetchProducts, fetchStats]);

  // ── Delete ──
  const handlePermanentDeleteMenuItem = useCallback(async (id) => {
    const res = await apiDeleteProduct(id);
    if (!res.success) { showSnackbar(res.error, 'error'); return; }
    showSnackbar('✅ Product permanently deleted successfully!');
    handleCloseDeleteDialog();
    fetchProducts();
    fetchStats();
    fetchCategories();
  }, [showSnackbar, fetchProducts, fetchStats, fetchCategories]);

  // ── Stock: fetch ──
  const fetchProductStock = useCallback(async (productId) => {
    setStockLoading(true);
    const res = await apiGetProductStock(productId);
    if (res.success) {
      setInventoryData(res.inventory);
      setStockMovements(res.movements || []);
    } else {
      setInventoryData(null);
      setStockMovements([]);
    }
    setStockLoading(false);
  }, []);

  // ── Stock: add ──
  const handleAddStock = useCallback(async () => {
    if (!stockItem) { showSnackbar('No item selected for stock addition', 'error'); return; }
    const quantity = parseFloat(stockQuantity);
    if (!quantity || quantity <= 0) { showSnackbar('Please enter a valid quantity greater than 0', 'error'); return; }

    setStockSubmitting(true);
    const res = await apiAddProductStock(stockItem.id, {
      quantity,
      cost_per_unit: stockCost ? parseFloat(stockCost) : null,
      supplier: stockSupplier?.trim() || null,
      notes: stockNotes?.trim() || null,
      unit: 'unit',
    });
    setStockSubmitting(false);

    if (!res.success) { showSnackbar(res.error, 'error'); return; }

    showSnackbar(`✅ Added ${quantity} units to "${stockItem.name}"`);
    handleCloseStockDialog();
    fetchProducts();
    fetchStats();
  }, [stockItem, stockQuantity, stockCost, stockSupplier, stockNotes, showSnackbar, fetchProducts, fetchStats]);

  // ── Stock: view ──
  const handleViewStock = useCallback((item) => {
    setViewStockItem(item);
    setViewStockDialogOpen(true);
    setInventoryData(null);
    setStockMovements([]);
    fetchProductStock(item.id);
  }, [fetchProductStock]);

  // ── Dialog handlers ──
  const handleOpenDialog = useCallback((mode, item = null) => {
    const today = new Date().toISOString().split('T')[0];
    setDialogMode(mode);
    setSelectedItem(item);
    setFormErrors({});
    setSubmitting(false);

    let newFormData;
    if (mode === 'create') {
      newFormData = { ...defaultFormData, created_date: today };
    } else if (item) {
      newFormData = {
        category: item.category || '',
        name: item.name || '',
        main_category: item.main_category || 'Food',
        description: item.description || '',
        price: item.price?.toString() || '',
        is_active: item.is_active !== undefined ? item.is_active : true,
        created_date: (item.created_date || today).split('T')[0],
      };
    } else {
      newFormData = defaultFormData;
    }
    setFormData(newFormData);
    setDialogOpen(true);
  }, [defaultFormData]);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedItem(null);
    setFormErrors({});
    setSubmitting(false);
    setFormData(defaultFormData);
  }, [defaultFormData]);

  const handleOpenToggleDialog = useCallback((item) => { setItemToToggle(item); setToggleDialogOpen(true); }, []);
  const handleCloseToggleDialog = useCallback(() => { setToggleDialogOpen(false); setItemToToggle(null); }, []);

  const handleOpenDeleteDialog = useCallback((item) => {
    setItemToDelete(item);
    setDeleteConfirmation('');
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
    setDeleteConfirmation('');
  }, []);

  const handleOpenStockDialog = useCallback((item) => {
    setStockItem(item);
    setStockQuantity('');
    setStockCost('');
    setStockSupplier('');
    setStockNotes('');
    setStockStep(0);
    setStockDialogOpen(true);
  }, []);

  const handleCloseStockDialog = useCallback(() => {
    setStockDialogOpen(false);
    setStockItem(null);
    setStockQuantity('');
    setStockCost('');
    setStockSupplier('');
    setStockNotes('');
    setStockStep(0);
    setStockSubmitting(false);
  }, []);

  const handleCloseViewStockDialog = useCallback(() => {
    setViewStockDialogOpen(false);
    setViewStockItem(null);
    setInventoryData(null);
    setStockMovements([]);
  }, []);

  // ── Form ──
  const handleFormChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors((prev) => ({ ...prev, [field]: '' }));
  }, [formErrors]);

  const validateForm = useCallback(() => {
    const errors = {};
    const trimmedName = formData?.name?.trim() || '';
    const trimmedCategory = formData?.category?.trim() || '';
    const price = parseFloat(formData?.price);

    if (!trimmedCategory) errors.category = 'Category is required';
    else if (trimmedCategory.length < 2) errors.category = 'Category must be at least 2 characters';

    if (!trimmedName) errors.name = 'Item name is required';
    else if (trimmedName.length < 2) errors.name = 'Item name must be at least 2 characters';
    else if (trimmedName.length > 100) errors.name = 'Item name must be 100 characters or less';

    if (!formData?.main_category) errors.main_category = 'Main category is required';

    if (!formData?.price) errors.price = 'Price is required';
    else if (isNaN(price) || price <= 0) errors.price = 'Price must be a positive number';
    else if (price > 999999.99) errors.price = 'Price cannot exceed 999,999.99';

    if (!formData?.created_date) errors.created_date = 'Created date is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);

    const submitData = {
      name: formData.name.trim(),
      category: formData.category.trim(),
      main_category: formData.main_category,
      description: formData.description?.trim() || null,
      price: parseFloat(formData.price),
      is_active: !!formData.is_active,
      created_date: formData.created_date,
    };

    if (dialogMode === 'create') {
      await handleCreateMenuItem(submitData);
    } else if (dialogMode === 'edit' && selectedItem) {
      await handleUpdateMenuItem(selectedItem.id, submitData);
    }
    setSubmitting(false);
  }, [formData, dialogMode, selectedItem, validateForm, handleCreateMenuItem, handleUpdateMenuItem]);

  // ── Filters ──
  const handleSearch = useCallback((event) => setSearchTerm(event.target.value), []);
  const handleClearSearch = useCallback(() => setSearchTerm(''), []);
  const handleMainCategoryFilterChange = useCallback((event, newValue) => {
    if (newValue !== null) { setMainCategoryFilter(newValue); setPage(0); }
  }, []);
  const handleCategoryFilterChange = useCallback((event) => { setCategoryFilter(event.target.value); setPage(0); }, []);
  const handlePriceRangeChange = useCallback((event, newValue) => { setPriceRange(newValue); setIsPriceFilterActive(true); setPage(0); }, []);
  const handleClearAllFilters = useCallback(() => {
    setSearchTerm('');
    setMainCategoryFilter('all');
    setCategoryFilter('all');
    setPriceRange([0, 100000]);
    setIsPriceFilterActive(false);
    setPage(0);
  }, []);

  const handleSort = useCallback((property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  }, [orderBy, order]);

  const handleRefresh = useCallback(() => {
    showSnackbar('Refreshing data...', 'info');
    fetchProducts();
    fetchStats();
    fetchCategories();
  }, [fetchProducts, fetchStats, fetchCategories, showSnackbar]);

  // ════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════
  return (
    <>
      <div className="epos-wrapper">
        <div className="epos-main-layout">
          <Sidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />

          <div className="epos-main">
            <Header />

            <div className="epos-content">
              <div className="hero-card">
                <div>
                  <div className="hero-title" style={{ color: locationColor, fontWeight: 800 }}>
                    {locationConfig.icon} {locationName} Menu Items
                  </div>
                  <div className="hero-text-wrapper">
                    <div className="hero-tagline">
                      <span className="hero-tagline-icon">✦</span>
                      Manage tuckshop items with inventory tracking
                    </div>
                  </div>
                </div>
                <button
                  className="new-order-btn"
                  onClick={() => handleOpenDialog('create')}
                  style={{ backgroundColor: locationColor }}
                >
                  <AddIcon style={{ marginRight: '8px' }} />
                  Add Menu Item
                </button>
              </div>

              <StatsCard stats={stats} locationColor={locationColor} />

              <Paper sx={{ p: 2, mb: 3, display: 'flex', flexDirection: 'column', gap: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: '200px' }}>
                    <TextField
                      size="small"
                      placeholder="Search items by name, category, or description..."
                      value={searchTerm}
                      onChange={handleSearch}
                      InputProps={{
                        startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>),
                        endAdornment: searchTerm && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={handleClearSearch}><ClearIcon fontSize="small" /></IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ flex: 1, maxWidth: '400px' }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {totalCount} items found
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Clear All Filters">
                      <IconButton onClick={handleClearAllFilters} color="primary" size="small"><ClearIcon /></IconButton>
                    </Tooltip>
                    <Tooltip title="Refresh">
                      <span>
                        <IconButton onClick={handleRefresh} disabled={loading} size="small"><RefreshIcon /></IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Filter by:</Typography>
                  </Box>

                  <ToggleButtonGroup value={mainCategoryFilter} exclusive onChange={handleMainCategoryFilterChange} size="small">
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="Food"><FoodIcon sx={{ fontSize: 16, mr: 0.5 }} /> Food</ToggleButton>
                    <ToggleButton value="Drinks"><DrinkIcon sx={{ fontSize: 16, mr: 0.5 }} /> Drinks</ToggleButton>
                  </ToggleButtonGroup>

                  <FormControl size="small" sx={{ minWidth: SELECT_MIN_WIDTH }}>
                    <InputLabel>Category</InputLabel>
                    <Select value={categoryFilter} onChange={handleCategoryFilterChange} label="Category">
                      <MenuItem value="all">All Categories</MenuItem>
                      {categories.map((cat) => (<MenuItem key={cat.id} value={cat.name}>{cat.name}</MenuItem>))}
                    </Select>
                  </FormControl>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: '200px' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Price: ₦{priceRange[0]} - ₦{priceRange[1]}
                      {isPriceFilterActive && (
                        <Chip label="Filter Active" size="small" color="primary" sx={{ ml: 1, height: 20, fontSize: '0.6rem' }} />
                      )}
                    </Typography>
                    <Slider value={priceRange} onChange={handlePriceRangeChange} valueLabelDisplay="auto" min={0} max={100000} size="small" sx={{ width: 200 }} />
                  </Box>
                </Box>
              </Paper>

              {loading && (<Box sx={{ width: '100%', mb: 2 }}><LinearProgress color="primary" /></Box>)}

              <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                        <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel active={orderBy === 'name'} direction={orderBy === 'name' ? order : 'asc'} onClick={() => handleSort('name')}>Item Name</TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel active={orderBy === 'category'} direction={orderBy === 'category' ? order : 'asc'} onClick={() => handleSort('category')}>Category</TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Main Category</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          <TableSortLabel active={orderBy === 'price'} direction={orderBy === 'price' ? order : 'asc'} onClick={() => handleSort('price')}>Price (₦)</TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                            <MenuIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="h6" color="textSecondary" gutterBottom sx={{ fontWeight: 600 }}>No Menu Items Found</Typography>
                            <Typography variant="body2" color="textSecondary">
                              {totalCount > 0 ? 'Try adjusting your search or filters' : 'Start by adding your first menu item'}
                            </Typography>
                            {totalCount > 0 && (
                              <Button variant="outlined" size="small" onClick={handleClearAllFilters} sx={{ mt: 2 }}>Clear All Filters</Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ) : (
                        items.map((item, index) => (
                          <MenuItemRow
                            key={item.id}
                            item={item}
                            index={page * rowsPerPage + index}
                            onView={handleOpenDialog}
                            onEdit={handleOpenDialog}
                            onToggle={handleOpenToggleDialog}
                            onDelete={handleOpenDeleteDialog}
                            onAddStock={handleOpenStockDialog}
                            onViewStock={handleViewStock}
                          />
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </Paper>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* ═══════ CREATE / EDIT / VIEW DIALOG ═══════ */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth
        TransitionComponent={Fade} PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}>
        <form onSubmit={handleSubmit}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {dialogMode === 'create' && <AddIcon color="primary" />}
              {dialogMode === 'edit' && <EditIcon color="info" />}
              {dialogMode === 'view' && <ViewIcon color="primary" />}
              <Typography variant="h6" fontWeight={700}>
                {dialogMode === 'create' && `Create Menu Item - ${locationName}`}
                {dialogMode === 'edit' && `Edit Menu Item - ${locationName}`}
                {dialogMode === 'view' && `Menu Item Details - ${locationName}`}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDialog} size="small"><CloseIcon /></IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ pt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!formErrors.category} disabled={dialogMode === 'view'} sx={{ minWidth: SELECT_MIN_WIDTH }}>
                  <InputLabel required sx={{ fontWeight: 500 }}>Category</InputLabel>
                  <Select value={formData?.category || ''} onChange={(e) => handleFormChange('category', e.target.value)} label="Category">
                    <MenuItem value="" disabled>Select a category</MenuItem>
                    {categories.map((cat) => (<MenuItem key={cat.id} value={cat.name}>{cat.name}</MenuItem>))}
                  </Select>
                  {formErrors.category && <FormHelperText>{formErrors.category}</FormHelperText>}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!formErrors.main_category} disabled={dialogMode === 'view'} sx={{ minWidth: SELECT_MIN_WIDTH }}>
                  <InputLabel required sx={{ fontWeight: 500 }}>Main Category</InputLabel>
                  <Select value={formData?.main_category || 'Food'} onChange={(e) => handleFormChange('main_category', e.target.value)} label="Main Category">
                    {MAIN_CATEGORIES.map(cat => (
                      <MenuItem key={cat} value={cat}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {cat === 'Food' ? <FoodIcon fontSize="small" /> : <DrinkIcon fontSize="small" />}
                          {cat}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.main_category && <FormHelperText>{formErrors.main_category}</FormHelperText>}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField fullWidth label="Item Name" value={formData?.name || ''}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  error={!!formErrors.name} helperText={formErrors.name}
                  disabled={dialogMode === 'view'} required
                  InputProps={{ startAdornment: (<InputAdornment position="start"><MenuIcon fontSize="small" color="action" /></InputAdornment>) }}
                  autoFocus={dialogMode !== 'view'}
                  placeholder="e.g., Meat Pie, Chin Chin, Coca-Cola" />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Price (₦)" type="number" value={formData?.price || ''}
                  onChange={(e) => handleFormChange('price', e.target.value)}
                  error={!!formErrors.price} helperText={formErrors.price}
                  disabled={dialogMode === 'view'} required
                  InputProps={{ startAdornment: (<InputAdornment position="start"><MoneyIcon fontSize="small" color="action" /></InputAdornment>), inputProps: { step: '0.01', min: '0.01' } }}
                  placeholder="0.00" />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={dialogMode === 'view'} sx={{ minWidth: SELECT_MIN_WIDTH }}>
                  <InputLabel sx={{ fontWeight: 500 }}>Status</InputLabel>
                  <Select value={formData?.is_active ? 'active' : 'inactive'} onChange={(e) => handleFormChange('is_active', e.target.value === 'active')} label="Status">
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField fullWidth label="Description" value={formData?.description || ''}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  disabled={dialogMode === 'view'} multiline rows={3}
                  placeholder="Brief description of the item"
                  InputProps={{ startAdornment: (<InputAdornment position="start" sx={{ alignItems: 'flex-start', pt: 1.5 }}><CategoryIcon fontSize="small" color="action" /></InputAdornment>) }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Created Date" type="date" value={formData?.created_date || ''}
                  onChange={(e) => handleFormChange('created_date', e.target.value)}
                  error={!!formErrors.created_date} helperText={formErrors.created_date}
                  disabled={dialogMode === 'view'} required
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ startAdornment: (<InputAdornment position="start"><CalendarIcon fontSize="small" color="action" /></InputAdornment>) }} />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={handleCloseDialog} color="inherit" sx={{ fontWeight: 500 }}>
              {dialogMode === 'view' ? 'Close' : 'Cancel'}
            </Button>
            {dialogMode !== 'view' && (
              <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={submitting}
                sx={{ backgroundColor: locationColor, fontWeight: 600, '&:hover': { backgroundColor: locationColor, opacity: 0.9 } }}>
                {submitting ? <CircularProgress size={24} color="inherit" /> : (dialogMode === 'create' ? 'Create Item' : 'Update Item')}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      {/* ═══════ TOGGLE DIALOG ═══════ */}
      <Dialog open={toggleDialogOpen} onClose={handleCloseToggleDialog} TransitionComponent={Fade}
        PaperProps={{ sx: { borderRadius: 2, p: 1, maxWidth: '500px' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {itemToToggle?.is_active ? <CancelIcon color="warning" /> : <CheckCircleIcon color="success" />}
          <Typography variant="h6" fontWeight={700}>
            {itemToToggle?.is_active ? 'Deactivate' : 'Activate'} Menu Item
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <InfoIcon color="info" sx={{ mt: 0.5 }} />
            <Typography variant="body1">
              Are you sure you want to <strong>{itemToToggle?.is_active ? 'deactivate' : 'activate'}</strong> menu item <strong>"{itemToToggle?.name}"</strong>?
            </Typography>
          </Box>
          <Alert severity={itemToToggle?.is_active ? 'warning' : 'info'}>
            {itemToToggle?.is_active
              ? 'Deactivating will hide this item from the menu. Students will not be able to purchase it.'
              : 'Activating will make this item visible in the menu again. Students will be able to purchase it.'}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseToggleDialog} color="inherit">Cancel</Button>
          <Button onClick={() => handleToggleMenuItemStatus(itemToToggle?.id)}
            variant="contained" color={itemToToggle?.is_active ? 'warning' : 'success'}
            startIcon={itemToToggle?.is_active ? <CancelIcon /> : <CheckCircleIcon />}>
            {itemToToggle?.is_active ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════ DELETE DIALOG ═══════ */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} TransitionComponent={Fade}
        PaperProps={{ sx: { borderRadius: 2, p: 1, maxWidth: '550px' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          <Typography variant="h6" fontWeight={700} color="error">Permanent Delete</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
            <WarningIcon color="error" sx={{ mt: 0.5, fontSize: 32 }} />
            <Box>
              <Typography variant="body1" gutterBottom>
                You are about to <strong style={{ color: '#d32f2f' }}>permanently delete</strong> menu item <strong>"{itemToDelete?.name}"</strong>.
              </Typography>
              <Typography variant="body2" color="textSecondary">
                This action <strong style={{ color: '#d32f2f' }}>cannot be undone</strong>.
              </Typography>
            </Box>
          </Box>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Warning:</strong> Deleting this item will remove it from all orders and menus associated with it.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={500} gutterBottom>
              To confirm deletion, type the item name <strong>"{itemToDelete?.name}"</strong> below:
            </Typography>
            <TextField fullWidth size="small" placeholder={`Type "${itemToDelete?.name}" to confirm`}
              value={deleteConfirmation} onChange={(e) => setDeleteConfirmation(e.target.value)}
              error={deleteConfirmation !== '' && deleteConfirmation !== itemToDelete?.name}
              helperText={deleteConfirmation !== '' && deleteConfirmation !== itemToDelete?.name ? 'Item name does not match' : ''}
              sx={{ mt: 1 }} autoFocus />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseDeleteDialog} color="inherit">Cancel</Button>
          <Button onClick={() => handlePermanentDeleteMenuItem(itemToDelete?.id)}
            variant="contained" color="error" startIcon={<DeleteIcon />}
            disabled={deleteConfirmation !== itemToDelete?.name}>
            Permanently Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════ ADD STOCK DIALOG — WIRED ═══════ */}
      <Dialog open={stockDialogOpen} onClose={handleCloseStockDialog} maxWidth="sm" fullWidth
        TransitionComponent={Fade} PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              <StockIcon sx={{ mr: 1, color: '#4caf50' }} />
              Add Stock to Menu Item
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stockItem?.name} - ₦{Number(stockItem?.price || 0).toFixed(2)}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseStockDialog} size="small" disabled={stockSubmitting}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 3 }}>
          <Stepper activeStep={stockStep} sx={{ mb: 3 }}>
            <Step><StepLabel>Quantity</StepLabel></Step>
            <Step><StepLabel>Details</StepLabel></Step>
            <Step><StepLabel>Confirm</StepLabel></Step>
          </Stepper>
          <Alert severity="info" sx={{ mb: 3 }}>
            Adding stock will create or update the inventory item linked to this menu item.
          </Alert>

          {stockStep === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField fullWidth label="Quantity to Add" type="number" value={stockQuantity}
                  onChange={(e) => setStockQuantity(e.target.value)} required autoFocus
                  InputProps={{ inputProps: { min: 0.01, step: 0.01 }, endAdornment: <InputAdornment position="end">unit</InputAdornment> }} />
              </Grid>
            </Grid>
          )}

          {stockStep === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField fullWidth label="Cost Per Unit (Optional)" type="number" value={stockCost}
                  onChange={(e) => setStockCost(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Supplier (Optional)" value={stockSupplier} onChange={(e) => setStockSupplier(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Notes (Optional)" multiline rows={3} value={stockNotes} onChange={(e) => setStockNotes(e.target.value)} />
              </Grid>
            </Grid>
          )}

          {stockStep === 2 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Confirm Stock Addition</Typography>
              <Box sx={{ bgcolor: alpha('#4caf50', 0.04), p: 2, borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Item</Typography>
                    <Typography variant="body2" fontWeight={500}>{stockItem?.name}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Quantity</Typography>
                    <Typography variant="body2" fontWeight={700} color="#4caf50">+{stockQuantity} units</Typography>
                  </Grid>
                  {stockCost && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Cost Per Unit</Typography>
                      <Typography variant="body2">₦{parseFloat(stockCost).toFixed(2)}</Typography>
                    </Grid>
                  )}
                  {stockSupplier && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Supplier</Typography>
                      <Typography variant="body2">{stockSupplier}</Typography>
                    </Grid>
                  )}
                  {stockNotes && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Notes</Typography>
                      <Typography variant="body2">{stockNotes}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseStockDialog} color="inherit" disabled={stockSubmitting}>Cancel</Button>
          {stockStep > 0 && <Button onClick={() => setStockStep(prev => prev - 1)} disabled={stockSubmitting}>Back</Button>}
          {stockStep < 2 ? (
            <Button variant="contained" onClick={() => setStockStep(prev => prev + 1)}
              disabled={stockStep === 0 && !stockQuantity}
              sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#388e3c' } }}>
              Next
            </Button>
          ) : (
            <Button variant="contained" onClick={handleAddStock} disabled={!stockQuantity || stockSubmitting}
              startIcon={stockSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#388e3c' } }}>
              {stockSubmitting ? 'Saving…' : 'Confirm Add Stock'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ═══════ VIEW STOCK DIALOG — WIRED ═══════ */}
      <Dialog open={viewStockDialogOpen} onClose={handleCloseViewStockDialog} maxWidth="lg" fullWidth
        TransitionComponent={Fade} PaperProps={{ sx: { borderRadius: 2, maxHeight: '85vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              <StoreIcon sx={{ mr: 1, color: theme.palette.info.main }} />
              Stock Information: {viewStockItem?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {viewStockItem?.category} • ₦{Number(viewStockItem?.price || 0).toFixed(2)}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseViewStockDialog} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {stockLoading ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading stock…</Typography>
            </Box>
          ) : inventoryData ? (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#4caf50', 0.04), borderLeft: '4px solid #4caf50' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Current Stock</Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {Number(inventoryData.quantity).toFixed(0)} {inventoryData.unit}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#ff9800', 0.04), borderLeft: '4px solid #ff9800' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Reorder Point</Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {Number(inventoryData.reorder_point).toFixed(0)} {inventoryData.unit}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#2196f3', 0.04), borderLeft: '4px solid #2196f3' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      {(() => {
                        const meta = STATUS_META[inventoryData.status] || STATUS_META.IN_STOCK;
                        return (
                          <Chip
                            label={meta.label}
                            size="small"
                            sx={{ fontWeight: 700, bgcolor: alpha(meta.bg, 0.12), color: meta.text }}
                          />
                        );
                      })()}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: alpha('#9c27b0', 0.04), borderLeft: '4px solid #9c27b0' }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Total Value</Typography>
                      <Typography variant="h5" fontWeight={700}>
                        ₦{(Number(inventoryData.quantity) * Number(inventoryData.cost_per_unit || 0)).toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                <HistoryIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
                Stock Movement History
              </Typography>

              {stockMovements.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="textSecondary">No stock movements recorded yet.</Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Change</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Before</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>After</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stockMovements.map((m) => {
                        const qty = Number(m.quantity);
                        const isIn = ['PURCHASE', 'RETURN', 'ADJUSTMENT'].includes(m.movement_type) && qty > 0;
                        return (
                          <TableRow key={m.id}>
                            <TableCell>{new Date(m.created_at).toLocaleString()}</TableCell>
                            <TableCell>
                              <Chip label={m.movement_type} size="small"
                                sx={{ bgcolor: alpha(isIn ? '#4caf50' : '#f44336', 0.1), color: isIn ? '#2e7d32' : '#c62828', fontWeight: 700 }}
                                icon={isIn ? <InIcon fontSize="small" /> : <OutIcon fontSize="small" />} />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, color: isIn ? '#2e7d32' : '#c62828' }}>
                              {isIn ? '+' : '−'}{Math.abs(qty)} {m.unit}
                            </TableCell>
                            <TableCell>{Number(m.previous_quantity).toFixed(0)}</TableCell>
                            <TableCell>{Number(m.new_quantity).toFixed(0)}</TableCell>
                            <TableCell>{m.reason || '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <InventoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">No inventory yet</Typography>
              <Typography variant="body2" color="textSecondary">
                Click <strong>Add Stock</strong> on this item to create its first inventory entry.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseViewStockDialog} color="inherit">Close</Button>
        </DialogActions>
      </Dialog>

      {/* ═══════ SNACKBAR ═══════ */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} TransitionComponent={Fade}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ═══════ FAB ═══════ */}
      <Fab color="primary" aria-label="add menu item"
        sx={{
          position: 'fixed', bottom: 24, right: 24,
          display: { xs: 'flex', sm: 'none' },
          zIndex: 1000,
          backgroundColor: locationColor,
          '&:hover': { backgroundColor: locationColor, opacity: 0.8 },
        }}
        onClick={() => handleOpenDialog('create')}>
        <AddIcon />
      </Fab>
    </>
  );
};

export default MenuItemList;